import { useState, useEffect, useCallback } from 'react';
import type { CompositeScore } from './useComparisonScores';

/**
 * Map of composite scores by comparison ID
 */
export interface ComparisonScoresMap {
  [comparisonId: string]: CompositeScore[];
}

interface UseMultipleComparisonScoresResult {
  scoresMap: ComparisonScoresMap;
  loading: boolean;
  error: string | null;
  refetchAllScores: () => void;
}

/**
 * useMultipleComparisonScores - Fetch composite scores for multiple comparisons
 *
 * This hook fetches LLM judge composite scores for a list of comparison IDs.
 * Results are cached in memory to avoid redundant API calls.
 *
 * @param comparisonIds - Array of comparison IDs to fetch scores for
 * @returns Object containing scoresMap (by comparison ID), loading state, and error
 */
export function useMultipleComparisonScores(
  comparisonIds: string[]
): UseMultipleComparisonScoresResult {
  const [scoresMap, setScoresMap] = useState<ComparisonScoresMap>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [refetchToken, setRefetchToken] = useState(0);

  const refetchAllScores = useCallback(() => {
    console.log('[useMultipleComparisonScores] Manual refetch triggered');
    setRefetchToken(prev => prev + 1);
  }, []);

  useEffect(() => {
    if (comparisonIds.length === 0) {
      setScoresMap({});
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;

    async function fetchAllScores() {
      console.log('[useMultipleComparisonScores] Starting fetch for:', comparisonIds.length, 'comparisons');
      setLoading(true);
      setError(null);

      const newScoresMap: ComparisonScoresMap = {};
      let failedFetches = 0;
      const fetchPromises = comparisonIds.map(async (comparisonId) => {
        try {
          const url = `/api/results/${comparisonId}/scores?includeComposite=true&_=${Date.now()}`;
          const response = await fetch(url, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
              'Content-Type': 'application/json'
            }
          });

          if (!response.ok) {
            if (response.status === 404) {
              // No scores for this comparison - this is OK
              console.log('[useMultipleComparisonScores] 404 for comparison:', comparisonId);
              return;
            }
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          const data = await response.json();
          if (data.compositeScores && data.compositeScores.length > 0) {
            newScoresMap[comparisonId] = data.compositeScores;
            console.log('[useMultipleComparisonScores] Loaded scores for:', comparisonId, data.compositeScores.length);
          }
        } catch (err) {
          console.error('[useMultipleComparisonScores] Fetch error for comparison:', comparisonId, err);
          failedFetches += 1;
          // Continue with other comparisons even if one fails
        }
      });

      await Promise.all(fetchPromises);

      if (!cancelled) {
        setScoresMap(newScoresMap);
        setLoading(false);
        if (failedFetches > 0) {
          const message = `Failed to fetch ${failedFetches} of ${comparisonIds.length} comparisons`;
          console.warn('[useMultipleComparisonScores] Partial failure:', message);
          setError(message);
        } else {
          setError(null);
        }
        console.log('[useMultipleComparisonScores] Fetch complete. Loaded scores for', Object.keys(newScoresMap).length, 'comparisons');
      }
    }

    fetchAllScores();

    return () => {
      cancelled = true;
    };
  }, [comparisonIds.join(','), refetchToken]); // Use join to create stable dependency

  return {
    scoresMap,
    loading,
    error,
    refetchAllScores,
  };
}
